/**
 * Event detail — the spine of the app and the entry point to volunteering.
 *
 * One component serves both portals with role-conditional sections, because
 * volunteering is driven from the event rather than a separate list.
 * SPEC.md §6.2.
 *
 * A member sees: who's going, RSVP, and the departments they can join.
 * An admin sees: the same page plus "Post volunteer work", team rosters and
 * "Remove event".
 */

import { useNavigate, useParams } from 'react-router-dom';
import { eventLinkLabel, EXPECT_VOL, formatLongDate, formatMoney, initials, KIND_LABEL, today } from '../data';
import { useClubData } from '../context/ClubContext';
import { useUI } from '../context/UIContext';
import { TrendingTag, trendingEventId } from '../components/EventCard';
import { DeptCard } from '../components/DeptCard';
import {
  ConfirmSheet,
  DeptTeamSheet,
  DepartmentSheet,
  EventHeadSheet,
  EventFiguresSheet,
  EventLinkSheet,
} from '../components/sheets/ClubSheets';
import { BackButton, Button, ButtonRow, Empty, Eyebrow } from '../components/ui';

export function EventDetailPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { openSheet, toast } = useUI();
  const {
    db,
    role,
    meId,
    toggleRsvp,
    joinDepartment,
    leaveDepartment,
    removeDepartment,
    removeEvent,
    setEventCompleted,
  } = useClubData();

  const isAdmin = role === 'admin';
  const backTo = isAdmin ? '/a/events' : '/m/events';
  const event = db.events.find((e) => e.id === eventId);

  if (!event) {
    return (
      <>
        <BackButton onClick={() => navigate(backTo)}>Back to events</BackButton>
        <Empty title="Event not found">It may have been removed by the committee.</Empty>
      </>
    );
  }

  const isPast = event.date < today();
  const going = event.rsvp
    .map((id) => db.members.find((m) => m.id === id))
    .filter((m) => !!m);
  const iAmIn = !!meId && event.rsvp.includes(meId);

  const depts = db.departments.filter((d) => d.eventId === event.id);
  const slots = depts.reduce((sum, d) => sum + d.needed, 0);
  const filled = depts.reduce(
    (sum, d) => sum + db.signups.filter((s) => s.deptId === d.id).length,
    0,
  );
  const myDeptIds = db.signups.filter((s) => s.memberId === meId).map((s) => s.deptId);
  const iVolunteer = depts.some((d) => myDeptIds.includes(d.id));

  async function rsvp(going) {
    try {
      await toggleRsvp(event.id, going);
      toast(going ? "You're in — see you there" : 'RSVP cancelled');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not save your RSVP');
    }
  }

  async function join(deptId) {
    try {
      await joinDepartment(deptId);
      toast('Signed up — the volunteer desk will confirm');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not sign up');
    }
  }

  async function leave(deptId) {
    try {
      await leaveDepartment(deptId);
      toast('Sign-up cancelled');
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not cancel that sign-up');
    }
  }

  const isDone = !!event.completedAt;
  const head = db.members.find((m) => m.id === event.headId);
  /* Not `isPast`. An event happening TODAY can be marked done — a 6 AM race is
     finished by breakfast — and the API draws the line the same way, refusing
     only a date still in the future. */
  const canComplete = event.date <= today();

  async function toggleCompleted(next) {
    try {
      await setEventCompleted(event.id, next);
      toast(
        next
          ? event.distanceKm
            ? `Completed — ${event.distanceKm} km added to the club total`
            : 'Event marked completed'
          : 'Event reopened',
      );
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Could not update that event');
    }
  }

  /* The event's own site, only when it is one we would put in an href — the
     API refuses anything else on the way in, and this is the second gate.
     `link` is null for "no page", which is what the whole row keys off. */
  const link = eventLinkLabel(event.linkUrl) ? event.linkUrl : null;

  /* The committee's typed totals. The API sends them to a member only while
     they are switched on, so for a member "has figures" already means
     "allowed to see them". Null means not entered — never printed as 0. */
  const hasFigures = event.registeredCount != null || event.amountCollected != null;
  const showFigures = isAdmin || (event.figuresPublic && hasFigures);

  const deptMode = isAdmin ? 'desk' : isPast ? 'view' : 'member';
  const isHot = trendingEventId(db.events, today()) === event.id;

  return (
    <>
      <BackButton onClick={() => navigate(backTo)}>Back to events</BackButton>

      {/* One clean white masthead for every event kind — the kind is a small
          brand-coloured label, not a colour scheme of its own. An event with a
          BANNER gets the poster hero instead: photo, ink gradient, same facts. */}
      <div className="mb-3.5 overflow-hidden border border-line bg-white">
        {event.image ? (
          <div className="relative">
            <img
              src={event.image}
              alt=""
              className="h-[200px] w-full object-cover min-[560px]:h-[260px]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink-900/90 via-ink-900/35 to-ink-900/5" />
            <div className="absolute inset-x-0 bottom-0 p-5">
              <div className="flex flex-wrap items-center gap-2 font-market text-[9.5px] uppercase tracking-[0.16em] text-gold-500">
                {KIND_LABEL[event.kind]}
                {isHot && <TrendingTag />}
              </div>
              <div className="mt-2 font-market text-[29px] font-extrabold leading-[1.05] tracking-[-0.03em] text-white">
                {event.title}
              </div>
              <div className="mt-2 font-market text-[12px] leading-[1.7] text-white/70">
                {formatLongDate(event.date)} · {event.time}
                {event.distanceKm ? ` · ${event.distanceKm} km` : ''}
                <br />
                {event.venue}
              </div>
            </div>
          </div>
        ) : (
          <div className="p-5">
            <div className="flex flex-wrap items-center gap-2 font-market text-[9.5px] uppercase tracking-[0.16em] text-gold-800">
              {KIND_LABEL[event.kind]}
              {isHot && <TrendingTag />}
            </div>
            <div className="mt-2 font-market text-[29px] font-extrabold leading-[1.05] tracking-[-0.03em] text-ink-700">
              {event.title}
            </div>
            <div className="mt-2.5 font-market text-[11.5px] leading-[1.7] text-copy-muted">
              {formatLongDate(event.date)} · {event.time}
              {event.distanceKm ? ` · ${event.distanceKm} km` : ''}
              <br />
              {event.venue}
            </div>
          </div>
        )}

        {/* The event's OWN site, when it has one — a race with its own entry
            form, a timing partner's page. Directly under the hero because a
            member who came here for the registration link should not have to
            read past the volunteer desk to find it. */}
        {(link || isAdmin) && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line p-4">
            <div className="min-w-0">
              <div className="font-market text-[9.5px] uppercase tracking-[0.13em] text-copy-muted">
                Event page
              </div>
              {link ? (
                <div className="mt-1.5 truncate font-market text-[13px] text-copy-muted" title={link}>
                  {eventLinkLabel(link)}
                </div>
              ) : (
                <div className="mt-1.5 font-market text-[11.5px] leading-relaxed text-copy-muted">
                  Add the race site or registration form and members get a link straight to it.
                </div>
              )}
            </div>
            <div className="flex flex-none flex-wrap gap-2">
              {link && (
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="inline-flex items-center justify-center gap-1.5 bg-ink-800 px-3.5 py-2 font-market text-[12.5px] font-extrabold tracking-[-0.005em] text-gold-500 transition-all duration-200 hover:-translate-y-px hover:bg-ink-700"
                >
                  Open event page
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M14 4h6v6" />
                    <path d="M20 4 10 14" />
                    <path d="M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" />
                  </svg>
                </a>
              )}
              {isAdmin && (
                <Button tone="g" small onClick={() => openSheet(<EventLinkSheet event={event} />)}>
                  {link ? 'Change' : 'Add link'}
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Registrations & collection — committee-typed, shown to members
            only when switched on. */}
        {showFigures && (
          <div className="border-t border-line p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="font-market text-[9.5px] uppercase tracking-[0.13em] text-copy-muted">
                Registrations &amp; collection
                {isAdmin && hasFigures && (
                  <span className={`ml-2 normal-case tracking-normal ${event.figuresPublic ? 'text-sea-600' : 'text-copy-label'}`}>
                    · {event.figuresPublic ? 'visible to members' : 'committee only'}
                  </span>
                )}
              </div>
              {isAdmin && (
                <Button tone="g" small onClick={() => openSheet(<EventFiguresSheet event={event} />)}>
                  {hasFigures ? 'Edit' : 'Add figures'}
                </Button>
              )}
            </div>
            {hasFigures ? (
              <div className="mt-2 grid grid-cols-2 gap-px border border-line bg-line">
                <div className="bg-white p-3">
                  <div className="font-market text-[24px] font-extrabold leading-none tabular-nums text-ink-700">
                    {event.registeredCount == null ? '—' : event.registeredCount.toLocaleString('en-IN')}
                  </div>
                  <div className="mt-1.5 font-market text-[9.5px] uppercase tracking-[0.13em] text-copy-label">
                    Registered
                  </div>
                </div>
                <div className="bg-white p-3">
                  <div className="font-market text-[24px] font-extrabold leading-none tabular-nums text-ink-700">
                    {event.amountCollected == null ? '—' : formatMoney(event.amountCollected)}
                  </div>
                  <div className="mt-1.5 font-market text-[9.5px] uppercase tracking-[0.13em] text-copy-label">
                    Collected
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-1.5 font-market text-[11.5px] leading-relaxed text-copy-muted">
                Enter how many registered and how much was collected. You choose whether members see it.
              </div>
            )}
          </div>
        )}

        {event.note && (
          <div className="border-t border-line p-4">
            <div className="text-[13.5px] leading-relaxed text-copy-muted">{event.note}</div>
          </div>
        )}

        {/* Who is running this event. Sits above the completion strip because
            that is the order the job happens in: appoint someone, they record
            what everyone did, then the committee closes it. */}
        {isAdmin && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-line p-4">
            <div>
              <div className="font-market text-[9.5px] uppercase tracking-[0.13em] text-copy-muted">
                Event head
              </div>
              <div className="mt-1.5 font-market text-[13.5px] font-extrabold tracking-[-0.02em] text-ink-700">
                {head ? head.name : 'Nobody assigned'}
              </div>
              <div className="mt-1 font-market text-[11.5px] leading-relaxed text-copy-muted">
                {head
                  ? isDone
                    ? 'Their job closed when you marked this completed.'
                    : 'They can log race results and volunteer hours for this event.'
                  : 'Put a member in charge and they can record everyone\u2019s results and hours.'}
              </div>
            </div>
            <Button
              tone="g"
              small
              onClick={() => openSheet(<EventHeadSheet db={db} event={event} />)}
            >
              {head ? 'Change' : 'Assign'}
            </Button>
          </div>
        )}

        {/* The club's distance board is driven from HERE: an event's kilometres
            count only once the committee says it happened. Admin-only, and
            deliberately the last row before the roster — it is the action the
            committee comes back for after a race. */}
        {isAdmin && (
          <div
            className={`flex flex-wrap items-center justify-between gap-3 border-t border-line p-4 ${
              isDone ? 'bg-sea-50' : ''
            }`}
          >
            <div>
              <div className="font-market text-[13.5px] font-extrabold tracking-[-0.02em] text-ink-700">
                {isDone ? 'Completed' : canComplete ? 'Did this event happen?' : 'Not run yet'}
              </div>
              <div className="mt-1 font-market text-[11.5px] leading-relaxed text-copy-muted">
                {isDone
                  ? event.distanceKm
                    ? `${event.distanceKm} km counted toward the club total.`
                    : 'No distance on this event, so it adds nothing to the club total.'
                  : canComplete
                    ? event.distanceKm
                      ? `Mark it completed to add ${event.distanceKm} km to the club total.`
                      : 'This event carries no distance, so completing it adds nothing.'
                    : `You can mark this completed from ${formatLongDate(event.date)}.`}
              </div>
            </div>
            {isDone ? (
              <Button tone="g" small onClick={() => void toggleCompleted(false)}>
                Undo
              </Button>
            ) : (
              <Button tone="d" small disabled={!canComplete} onClick={() => void toggleCompleted(true)}>
                Mark completed
              </Button>
            )}
          </div>
        )}

        <div className="border-t border-line p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2.5">
            <div className="font-market text-[9.5px] uppercase tracking-[0.13em] text-copy-muted">
              Who&rsquo;s going · {going.length}
            </div>
            {!isAdmin &&
              !isPast &&
              (iAmIn ? (
                <Button tone="g" small onClick={() => void rsvp(false)}>
                  Cancel my RSVP
                </Button>
              ) : (
                <Button tone="p" small onClick={() => void rsvp(true)}>
                  I&rsquo;m in
                </Button>
              ))}
          </div>

          {going.length ? (
            <div className="flex flex-wrap gap-1.5">
              {going.map((m) => (
                <div
                  key={m.id}
                  className="grid h-[34px] w-[34px] place-items-center overflow-hidden rounded-full bg-line font-market text-[12px] font-bold text-copy-muted"
                  title={m.name}
                >
                  {m.photo ? (
                    <img src={m.photo} alt="" className="h-full w-full object-cover" />
                  ) : (
                    initials(m.name)
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="font-market text-[11.5px] text-copy-label">
              No one has RSVP&rsquo;d yet.
            </div>
          )}
        </div>
      </div>

      <Eyebrow>Volunteering for this event</Eyebrow>

      {depts.length ? (
        <>
          {!isAdmin && !isPast && (
            <div className="mb-3 border border-sea-300 bg-sea-50 p-4">
              <div className="font-market text-[19px] font-extrabold tracking-[-0.025em]">
                {iVolunteer ? "You're volunteering here" : 'Help run this event'}
              </div>
              <div className="mt-1.5 text-[12.5px] leading-relaxed text-copy-muted">
                {filled} of {slots} volunteer slots filled across {depts.length} departments. Pick
                the work that suits you — hours count toward your yearly {EXPECT_VOL}.
              </div>
            </div>
          )}

          {depts.map((d) => (
            <DeptCard
              key={d.id}
              dept={d}
              db={db}
              meId={meId}
              mode={deptMode}
              onJoin={join}
              onLeave={leave}
              onViewTeam={(id) => {
                const dept = db.departments.find((x) => x.id === id);
                if (dept) openSheet(<DeptTeamSheet dept={dept} db={db} />);
              }}
              onRemove={(id) => {
                const dept = db.departments.find((x) => x.id === id);
                const signed = db.signups.filter((sx) => sx.deptId === id).length;
                openSheet(
                  <ConfirmSheet
                    title={`Remove ${dept?.name ?? 'this work'}?`}
                    body={
                      signed
                        ? `${signed} member${signed === 1 ? ' has' : 's have'} signed up for this. Removing it takes their place${signed === 1 ? '' : 's'} with it.`
                        : 'Nobody has signed up yet, so nothing else is affected.'
                    }
                    confirmLabel="Remove work"
                    doneMessage="Work removed"
                    onConfirm={() => removeDepartment(id)}
                  />,
                );
              }}
            />
          ))}
        </>
      ) : (
        <Empty title="No volunteer work posted" padding={30}>
          {isAdmin
            ? 'Post a department below so members can sign up.'
            : 'The committee has not asked for volunteers on this event.'}
        </Empty>
      )}

      {isAdmin && (
        <div className="mt-3">
          <ButtonRow>
            <Button
              tone="v"
              onClick={() => openSheet(<DepartmentSheet db={db} presetEventId={event.id} />)}
            >
              + Post volunteer work
            </Button>
            <Button
              tone="g"
              onClick={() =>
                openSheet(
                  <ConfirmSheet
                    title={`Remove ${event.title}?`}
                    body={
                      depts.length
                        ? `This also removes ${depts.length} volunteer department${depts.length === 1 ? '' : 's'} and every sign-up on ${depts.length === 1 ? 'it' : 'them'}. It cannot be undone.`
                        : 'The event and any RSVPs go with it. This cannot be undone.'
                    }
                    confirmLabel="Remove event"
                    doneMessage="Event removed"
                    onConfirm={async () => {
                      await removeEvent(event.id);
                      navigate('/a/events');
                    }}
                  />,
                )
              }
            >
              Remove event
            </Button>
          </ButtonRow>
        </div>
      )}
    </>
  );
}
